import {
  MarketData,
  RatesContext,
  RatesType,
} from "@/lib/context/rates-context";
import { getCoinGeckoMarketData } from "@/lib/helpers/coingecko";
import { mapMarketData } from "@/lib/helpers/token-mapping";
import { useWidget } from "@/lib/hooks/use-widget";
import { useStytchUser } from "@stytch/react";
import React, {
  FC,
  PropsWithChildren,
  useEffect,
  useReducer,
  useState,
} from "react";

const PROD_MARKETS_WSS_BASE = `wss://markets-ws.api.bitcoin.com/v1/live-price`;

type RatesPayload = {
  baseCcy: string;
  price: number;
};

const ratesReducer = (
  state: RatesType,
  { baseCcy, price }: RatesPayload,
): RatesType => ({
  ...state,
  [baseCcy]: price,
});

const RatesProvider: FC<PropsWithChildren> = ({ children }) => {
  const [rates, dispatchRates] = useReducer(ratesReducer, {});
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useStytchUser();
  const {
    balances,
    isLoading: isWidgetLoading,
    tokensArray,
    baseUrl,
  } = useWidget();
  const [marketData, setMarketData] = useState<MarketData[]>([]);

  const currency = (user?.untrusted_metadata.currency as string) || "USD";

  useEffect(() => {
    if (isWidgetLoading || !user || Object.keys(balances).length === 0) {
      return;
    }

    const crypto = Object.values(balances).flatMap(t =>
      t.map(tt => tt.symbol.toUpperCase()),
    );

    const socket = new WebSocket(PROD_MARKETS_WSS_BASE);

    socket.onopen = () => {
      socket.send(
        JSON.stringify({
          type: "SUBSCRIBE",
          crypto,
          fiat: [user.untrusted_metadata.currency || "USD"],
        }),
      );
    };

    socket.onmessage = ev => {
      if (typeof ev.data !== "string") return;
      const data = JSON.parse(ev.data);

      if (data.message !== "PRICE_UPDATE") return;
      dispatchRates(data);
      setIsLoading(false);
    };
    return () => {
      socket.close();
    };
  }, [isWidgetLoading, balances]);

  useEffect(() => {
    const getCoinMarketData = async () => {
      const tokenIds = tokensArray.map(t => t.id);

      const data = await getCoinGeckoMarketData(
        baseUrl,
        currency,
        tokenIds.join(","),
      );

      if (!data) return;

      const formattedData = mapMarketData(data);

      setMarketData(formattedData);
    };

    if (!isLoading) {
      getCoinMarketData();
    }
  }, [tokensArray, isLoading, baseUrl, currency]);

  return (
    <RatesContext.Provider value={{ rates, isLoading, marketData }}>
      {children}
    </RatesContext.Provider>
  );
};

export default RatesProvider;
